import React from 'react';
import {
  XMarkIcon,
  UserIcon,
  MapPinIcon,
  PhoneIcon,
  BuildingOfficeIcon,
  IdentificationIcon,
  HeartIcon
} from '@heroicons/react/24/outline';

const BeneficiaryDetailsModal = ({ beneficiary, isOpen, onClose }) => {
  if (!isOpen || !beneficiary) return null;

  const getSafeLabel = (value) => {
    if (!value) return 'Non spécifié';

    // Valeurs simples
    if (typeof value === 'string' || typeof value === 'number') {
      return value;
    }

    // Objets avec libelle potentiellement imbriqué
    if (typeof value === 'object') {
      const lib = value.libelle;

      if (typeof lib === 'string' || typeof lib === 'number') {
        return lib;
      }

      if (lib && typeof lib === 'object') {
        const nestedLib = lib.libelle;
        if (typeof nestedLib === 'string' || typeof nestedLib === 'number') {
          return nestedLib;
        }
      }

      return 'Non spécifié';
    }

    // Fallback ultime en chaîne
    return String(value);
  };

  // Calculer l'âge
  const calculateAge = (dateNaissance) => {
    if (!dateNaissance) return 'N/A';
    const today = new Date();
    const birthDate = new Date(dateNaissance);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  // Formater la date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Obtenir la couleur du statut (codes internes + libellés français)
  const getStatusColor = (status) => {
    switch (status) {
      case 'ACTIF':
      case 'Actif(ve)':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'INACTIF':
      case 'Radié(e)':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'SUSPENDU':
      case 'Suspendu(e)':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const isAdherent = beneficiary.type === 'ADHERENT';

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-4 mx-auto p-5 border w-11/12 md:w-4/5 lg:w-3/4 xl:w-2/3 shadow-lg rounded-lg bg-white max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6 pb-4 border-b">
          <div className="flex items-center space-x-4">
            <div className={`h-16 w-16 rounded-full flex items-center justify-center ${
              isAdherent ? 'bg-primary-600' : 'bg-blue-600'
            }`}>
              <span className="text-xl font-bold text-white">
                {beneficiary.nom?.[0]}{beneficiary.prenoms?.[0]}
              </span>
            </div>
            <div>
              <h3 className="text-2xl font-bold text-gray-900">
                {beneficiary.nom} {beneficiary.prenoms}
              </h3>
              <div className="flex items-center space-x-3 mt-1">
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor(getSafeLabel(beneficiary.status))}`}>
                  {getSafeLabel(beneficiary.status)}
                </span>
                <span className="text-sm text-gray-500">
                  {isAdherent ? 'Adhérent Principal' : getSafeLabel(beneficiary.lienParente)}
                </span>
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Contenu */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Colonne gauche : Informations personnelles */}
          <div className="space-y-6">
            <div>
              <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <UserIcon className="h-5 w-5 mr-2 text-primary-600" />
                Informations personnelles
              </h4>
              <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-500">Matricule</span>
                  <span className="text-sm text-gray-900 font-mono">{beneficiary.matricule}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-500">Genre</span>
                  <span className="text-sm text-gray-900">{getSafeLabel(beneficiary.genre)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-500">Date de naissance</span>
                  <span className="text-sm text-gray-900">{formatDate(beneficiary.dateNaissance)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-500">Âge</span>
                  <span className="text-sm text-gray-900">{calculateAge(beneficiary.dateNaissance)} ans</span>
                </div>
                {beneficiary.lieuNaissance && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-500">Lieu de naissance</span>
                    <span className="text-sm text-gray-900">{beneficiary.lieuNaissance}</span>
                  </div>
                )}
                {beneficiary.nationalite && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-500">Nationalité</span>
                    <span className="text-sm text-gray-900">{getSafeLabel(beneficiary.nationalite)}</span>
                  </div>
                )}
                {beneficiary.situationMatrimoniale && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-500">Situation matrimoniale</span>
                    <span className="text-sm text-gray-900">
                      {getSafeLabel(beneficiary.situationMatrimoniale)}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Section relation familiale masquée pour les ayants-droit côté mutualiste */}
          </div>

          {/* Colonne droite : Adresse + Contact + Infos système */}
          <div className="space-y-6">
            {/* Adresse */}
            <div>
              <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <MapPinIcon className="h-5 w-5 mr-2 text-primary-600" />
                Adresse de résidence
              </h4>
              <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                <div className="flex items-start justify-between">
                  <span className="text-sm font-medium text-gray-500">Adresse</span>
                  <span className="text-sm text-gray-900 text-right">{beneficiary.adresseResidence}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-500">Ville / Pays</span>
                  <span className="text-sm text-gray-900">
                    {beneficiary.ville} / {beneficiary.pays || "Côte d'Ivoire"}
                  </span>
                </div>
                {beneficiary.codePostal && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-500">Code postal</span>
                    <span className="text-sm text-gray-900">{beneficiary.codePostal}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Contact */}
            <div>
              <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <PhoneIcon className="h-5 w-5 mr-2 text-primary-600" />
                Contact
              </h4>
              <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-500">Mobile principal</span>
                  <span className="text-sm text-gray-900">{beneficiary.mobile1 || 'Non renseigné'}</span>
                </div>
                {beneficiary.mobile2 && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-500">Mobile secondaire</span>
                    <span className="text-sm text-gray-900">{beneficiary.mobile2}</span>
                  </div>
                )}
                {beneficiary.emailPersonnel && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-500">Email personnel</span>
                    <span className="text-sm text-gray-900">{beneficiary.emailPersonnel}</span>
                  </div>
                )}
                {beneficiary.adressePostale && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-500">Adresse postale</span>
                    <span className="text-sm text-gray-900">{beneficiary.adressePostale}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Informations professionnelles en pleine largeur (pour adhérents) */}
        {isAdherent && (beneficiary.enterprise || beneficiary.fonction) && (
          <div className="mt-8">
            <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <BuildingOfficeIcon className="h-5 w-5 mr-2 text-primary-600" />
              Informations professionnelles
            </h4>
            <div className="bg-gray-50 rounded-lg p-4 space-y-3">
              {beneficiary.enterprise && (
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-500">Entreprise</span>
                  <span className="text-sm text-gray-900">{getSafeLabel(beneficiary.enterprise.nom || beneficiary.enterprise)}</span>
                </div>
              )}
              {beneficiary.fonction && (
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-500">Fonction</span>
                  <span className="text-sm text-gray-900">{getSafeLabel(beneficiary.fonction)}</span>
                </div>
              )}
              {beneficiary.emailProfessionnel && (
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-500">Email professionnel</span>
                  <span className="text-sm text-gray-900">{beneficiary.emailProfessionnel}</span>
                </div>
              )}
              {beneficiary.telephoneProfessionnel && (
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-500">Téléphone professionnel</span>
                  <span className="text-sm text-gray-900">{beneficiary.telephoneProfessionnel}</span>
                </div>
              )}
              {beneficiary.adresseProfessionnelle && (
                <div className="flex items-start justify-between">
                  <span className="text-sm font-medium text-gray-500">Adresse professionnelle</span>
                  <span className="text-sm text-gray-900 text-right">{beneficiary.adresseProfessionnelle}</span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      <div className="flex justify-end space-x-3 mt-8 pt-6 border-t">
        <button
          onClick={onClose}
          className="btn-secondary"
        >
          Fermer
        </button>
      </div>
    </div>
  );
};

export default BeneficiaryDetailsModal;
